import { useEffect, useState } from "react";
import { api, API, uploadSoftwareChunked, formatApiErrorDetail } from "../lib/api";
import { Layout } from "../components/Layout";
import { toast } from "sonner";
import {
  Package, Lightning, UploadSimple, LinkSimple, Terminal, FloppyDisk, Archive,
  ClipboardText, DownloadSimple, CheckCircle, Sparkle, ListChecks,
} from "@phosphor-icons/react";

const labelCls = "block font-mono text-[10px] uppercase tracking-wider text-[#71717A] mb-2";
const inputCls = "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200";

const DEFAULTS = {
  app_name: "", version: "1.0", source_url: "", pkg_type: "auto",
  app_bundle_name: "", pre_script: "", post_script: "", ai_goal: "",
};

export default function CustomSoftware() {
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("addigy");
  const [values, setValues] = useState(DEFAULTS);
  const [mode, setMode] = useState("upload"); // upload | url
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploaded, setUploaded] = useState(null); // { filename, url }
  const [building, setBuilding] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiExplain, setAiExplain] = useState([]);
  const [result, setResult] = useState(null);
  const [projects, setProjects] = useState([]);
  const [saveTarget, setSaveTarget] = useState("new");
  const [saving, setSaving] = useState(false);
  const [bundling, setBundling] = useState(false);

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const setVal = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okExt = /\.(pkg|dmg|zip|app)$/i.test(file.name);
    if (!okExt) { toast.error("Choose a .pkg, .dmg, or .zip (zipped .app) file"); return; }
    if (file.size > 500 * 1024 * 1024) { toast.error("File over 500MB — use 'Paste URL' instead"); return; }
    setUploading(true); setProgress(0); setUploaded(null);
    try {
      const data = await uploadSoftwareChunked(file, setProgress);
      setUploaded({ filename: data.filename, url: data.url });
      setVal("source_url", data.url);
      if (!values.app_name) setVal("app_name", file.name.replace(/\.(pkg|dmg|zip|app)$/i, ""));
      toast.success("Uploaded to secure storage");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const build = async () => {
    if (!values.source_url.trim()) { toast.error("Upload a file or paste a package URL first"); return; }
    setBuilding(true);
    try {
      const { data } = await api.post("/custom-software/build", { provider, params: values });
      setResult(data);
      toast.success("Deploy script generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Build failed");
    } finally {
      setBuilding(false);
    }
  };

  const artifact = result?.artifacts?.[0];

  const aiAssist = async () => {
    if (!values.app_name.trim()) { toast.error("Enter an app name first so the AI knows what to script"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/custom-software/ai-scripts", {
        app_name: values.app_name, version: values.version, pkg_type: values.pkg_type, provider,
        goal: values.ai_goal,
      });
      setValues((s) => ({ ...s, pre_script: data.pre_script || s.pre_script, post_script: data.post_script || s.post_script }));
      setAiExplain(Array.isArray(data.explanation) ? data.explanation : []);
      toast.success("AI drafted your install steps — review before deploying");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI assist failed");
    } finally {
      setAiLoading(false);
    }
  };

  const copyOut = async () => {
    try {
      await navigator.clipboard.writeText(artifact.content);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed — select the script manually");
    }
  };
  const downloadOut = () => {
    const blob = new Blob([artifact.content], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = artifact.filename; a.click();
  };

  const save = async () => {
    if (!artifact) return;
    setSaving(true);
    try {
      const body = { title: `Custom SW: ${result.name}`, provider, output_type: "script",
        content: artifact.content, language: "bash", filename: artifact.filename,
        deploy_notes: result.deploy_notes, prompt: "" };
      if (saveTarget === "new") await api.post("/projects", body);
      else await api.post(`/projects/${saveTarget}/versions`, { ...body, note: "Custom software" });
      toast.success("Saved to projects");
      api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const downloadBundle = async () => {
    if (!artifact) return;
    setBundling(true);
    try {
      const resp = await fetch(`${API}/bundle`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: `Custom SW ${result.name}`, provider, output_type: "script",
          filename: artifact.filename, content: artifact.content, deploy_notes: result.deploy_notes }),
      });
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `${provider}-custom-software.zip`; a.click();
    } catch {
      toast.error("Bundle failed");
    } finally { setBundling(false); }
  };

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl tracking-tight flex items-center gap-2">
            <Package size={22} weight="fill" className="text-[#00F0FF]" /> Custom Software
          </h1>
          <p className="text-sm text-[#A1A1AA] font-mono mt-1">
            Upload your own installer, add scripts, and package it into a deploy script for any MDM.
          </p>
        </div>
        <span className="hidden md:flex items-center gap-2 font-mono text-xs text-[#00FF41] border border-[#00FF41]/40 bg-[#00FF41]/5 px-3 py-1.5">
          <Lightning size={13} weight="fill" /> 0 LLM TOKENS
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-px bg-[#27272A]">
        <div className="bg-[#0A0A0B] p-8 space-y-6">
          <div>
            <label className={labelCls}>MDM Provider</label>
            <div className="flex flex-wrap gap-2">
              {providers.map((p) => (
                <button key={p.id} data-testid={`cs-provider-${p.id}`} onClick={() => setProvider(p.id)}
                  className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${provider === p.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />{p.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>App name</label>
              <input data-testid="cs-app-name" value={values.app_name} onChange={(e) => setVal("app_name", e.target.value)} className={inputCls} placeholder="Acme Client" />
            </div>
            <div>
              <label className={labelCls}>Version</label>
              <input data-testid="cs-version" value={values.version} onChange={(e) => setVal("version", e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Source */}
          <div>
            <label className={labelCls}>Package source</label>
            <div className="flex gap-2 mb-3">
              <button data-testid="cs-mode-upload" onClick={() => setMode("upload")}
                className={`flex items-center gap-2 px-3 py-2 border text-xs ${mode === "upload" ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#71717A]"}`}>
                <UploadSimple size={14} /> Upload file
              </button>
              <button data-testid="cs-mode-url" onClick={() => setMode("url")}
                className={`flex items-center gap-2 px-3 py-2 border text-xs ${mode === "url" ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#71717A]"}`}>
                <LinkSimple size={14} /> Paste URL
              </button>
            </div>

            {mode === "upload" ? (
              <div className="border border-dashed border-[#27272A] p-5 text-center">
                <label className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] px-4 py-2 text-sm cursor-pointer hover:bg-[#00F0FF]/10 transition-colors">
                  <UploadSimple size={16} /> {uploading ? "Uploading…" : "Choose .pkg / .dmg / .zip"}
                  <input type="file" accept=".pkg,.dmg,.zip,.app" className="hidden" data-testid="cs-file-input" disabled={uploading} onChange={handleFile} />
                </label>
                <p className="text-[10px] font-mono text-[#71717A] mt-2">Up to 500MB, uploaded in chunks to secure storage. Larger? Use “Paste URL”.</p>
                {uploading && (
                  <div className="mt-3">
                    <div className="h-1.5 bg-[#27272A] overflow-hidden"><div className="h-full bg-[#00F0FF] transition-all" style={{ width: `${progress}%` }} data-testid="cs-progress" /></div>
                    <span className="font-mono text-[10px] text-[#00F0FF]">{progress}%</span>
                  </div>
                )}
                {uploaded && !uploading && (
                  <p className="mt-3 flex items-center justify-center gap-2 text-[11px] font-mono text-[#00FF41]" data-testid="cs-uploaded">
                    <CheckCircle size={14} weight="fill" /> {uploaded.filename}
                  </p>
                )}
              </div>
            ) : (
              <input data-testid="cs-url-input" value={values.source_url} onChange={(e) => { setVal("source_url", e.target.value); setUploaded(null); }} className={inputCls} placeholder="https://cdn.example.com/acme-client.pkg" />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Package type</label>
              <select data-testid="cs-pkg-type" value={values.pkg_type} onChange={(e) => setVal("pkg_type", e.target.value)} className={inputCls}>
                <option value="auto">Auto-detect from file</option>
                <option value="pkg">.pkg installer</option>
                <option value="dmg">.dmg (drag-to-Applications)</option>
                <option value="zip">.zip / .app bundle</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>What do you want the AI to do? (optional goal)</label>
            <textarea data-testid="cs-ai-goal" value={values.ai_goal} onChange={(e) => setVal("ai_goal", e.target.value)} rows={2}
              className={inputCls + " resize-y"}
              placeholder='e.g. "Silence the first-run setup, register the license key from a config, and start the background agent"' />
            <div className="flex items-center justify-between gap-3 mt-2">
              <p className="text-[10px] font-mono text-[#71717A]">Describe the outcome in plain English — the AI writes the pre/post-install steps to match.</p>
              <button data-testid="cs-ai-generate-btn" onClick={aiAssist} disabled={aiLoading}
                className="flex items-center gap-1.5 shrink-0 bg-[#B37FEB] text-[#0A0A0B] font-medium px-4 py-2 text-xs hover:bg-[#a066e0] transition-colors duration-200 disabled:opacity-50">
                <Sparkle size={14} weight="fill" /> {aiLoading ? "Generating…" : "Generate scripts with AI"}
              </button>
            </div>
          </div>
          {aiExplain.length > 0 && (
            <div data-testid="cs-ai-explain" className="border border-[#B37FEB]/40 bg-[#B37FEB]/5 p-4">
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#C79BF0] mb-3">
                <ListChecks size={14} weight="fill" /> What these scripts do
              </div>
              <ul className="space-y-2">
                {aiExplain.map((line, i) => (
                  <li key={i} className="flex gap-2 text-[13px] text-[#C7C7CC] leading-relaxed">
                    <span className="text-[#B37FEB] shrink-0">›</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[10px] font-mono text-[#71717A] mt-3">Review the generated bash below before deploying fleet-wide.</p>
            </div>
          )}
          <div>
            <label className={labelCls}>Pre-install script (optional, bash)</label>
            <textarea data-testid="cs-pre-script" value={values.pre_script} onChange={(e) => setVal("pre_script", e.target.value)} rows={3}
              className={inputCls + " resize-y"} placeholder='# e.g. /bin/launchctl bootout system/com.acme.agent 2>/dev/null || true' />
          </div>
          <div>
            <label className={labelCls}>Post-install script (optional, bash)</label>
            <textarea data-testid="cs-post-script" value={values.post_script} onChange={(e) => setVal("post_script", e.target.value)} rows={3}
              className={inputCls + " resize-y"} placeholder='# e.g. /usr/bin/open -a "Acme Client"' />
          </div>

          <button data-testid="cs-build-btn" onClick={build} disabled={building || uploading}
            className="flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-5 py-2.5 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50">
            <Lightning size={16} weight="fill" /> {building ? "Building…" : "Build deploy script (0 tokens)"}
          </button>
        </div>

        {/* Output */}
        <div className="bg-[#0A0A0B] p-8">
          <div className="flex items-center gap-2 mb-4 font-mono text-xs text-[#71717A] uppercase tracking-wider">
            <Terminal size={14} /> Output
          </div>
          {!result && <p className="text-sm text-[#71717A] font-mono">Configure and build to generate your deploy script.</p>}
          {result && artifact && (
            <div data-testid="cs-output">
              <div className="flex items-center gap-2 mb-3">
                <button data-testid="cs-copy-btn" onClick={copyOut} className="flex items-center gap-1.5 border border-[#27272A] px-3 py-1.5 text-xs hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"><ClipboardText size={13} /> Copy</button>
                <button data-testid="cs-download-btn" onClick={downloadOut} className="flex items-center gap-1.5 border border-[#27272A] px-3 py-1.5 text-xs hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"><DownloadSimple size={13} /> Download</button>
              </div>
              <pre className="bg-[#050505] border border-[#27272A] p-4 overflow-auto max-h-[380px] font-mono text-[11px] text-[#C7C7CC] leading-relaxed whitespace-pre-wrap">{artifact.content}</pre>
              <p className="text-[10px] font-mono text-[#71717A] mt-3">{result.deploy_notes}</p>
              <div className="flex items-center gap-2 mt-4">
                <select data-testid="cs-save-target" value={saveTarget} onChange={(e) => setSaveTarget(e.target.value)} className={inputCls + " flex-1"}>
                  <option value="new">+ New project</option>
                  {projects.map((p) => (<option key={p.id} value={p.id}>↳ Add version to: {p.title}</option>))}
                </select>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <button data-testid="cs-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors disabled:opacity-50"><FloppyDisk size={16} /> {saving ? "…" : "Save"}</button>
                <button data-testid="cs-bundle-btn" onClick={downloadBundle} disabled={bundling} className="flex items-center gap-2 border border-[#0A84FF] text-[#0A84FF] px-4 py-2 text-sm hover:bg-[#0A84FF]/10 transition-colors disabled:opacity-50"><Archive size={16} /> {bundling ? "…" : "Bundle"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
